/**
 * Chinese music theory for the composed tracks: keys, the five modes (宫商角徵羽) of the pentatonic,
 * and jianpu (简谱) degrees. A track says "1 = D": degree 1 is 宫 on D, and its mode names which
 * degree is the tonic (a 羽 piece in 1 = D rests on B). Pure functions, no audio.
 */

export type Mode = 'gong' | 'shang' | 'jue' | 'zhi' | 'yu';

export const MODES: readonly Mode[] = ['gong', 'shang', 'jue', 'zhi', 'yu'];

/** Names of the modes, for the review page and the studio. */
export const MODE_NAMES: Record<Mode, { readonly han: string; readonly thai: string }> = {
  gong: { han: '宫', thai: 'กง' },
  shang: { han: '商', thai: 'ซาง' },
  jue: { han: '角', thai: 'เจวี๋ย' },
  zhi: { han: '徵', thai: 'จื่อ' },
  yu: { han: '羽', thai: 'อวี่' },
};

/** The jianpu degree each mode rests on: 宫 1, 商 2, 角 3, 徵 5, 羽 6. */
export const MODE_TONIC: Record<Mode, number> = { gong: 1, shang: 2, jue: 3, zhi: 5, yu: 6 };

/** Semitones above 宫 of jianpu degrees 1–7 (4 is 清角, 7 is 变宫). */
export const DEGREE_SEMITONES: readonly number[] = [0, 2, 4, 5, 7, 9, 11];

/** The five degrees of the pentatonic, in order. */
export const PENTATONIC: readonly number[] = [1, 2, 3, 5, 6];

export type Key = 'C' | 'Db' | 'D' | 'Eb' | 'E' | 'F' | 'Gb' | 'G' | 'Ab' | 'A' | 'Bb' | 'B';

const KEY_PC: Record<Key, number> = {
  C: 0,
  Db: 1,
  D: 2,
  Eb: 3,
  E: 4,
  F: 5,
  Gb: 6,
  G: 7,
  Ab: 8,
  A: 9,
  Bb: 10,
  B: 11,
};

/** MIDI number of degree 1 in the middle octave (C4 … B4). */
export const keyMidi = (key: Key): number => 60 + KEY_PC[key];

/**
 * MIDI number of a jianpu degree (1–7) in a key, `octave` octaves up (negative: down), with a
 * sharp (+1) or flat (−1).
 */
export function degreeMidi(key: Key, degree: number, octave = 0, accidental = 0): number {
  if (!(degree >= 1 && degree <= 7) || !Number.isInteger(degree)) throw new Error(`bad degree ${degree}`);
  return keyMidi(key) + DEGREE_SEMITONES[degree - 1]! + 12 * octave + accidental;
}

/** Semitone steps of a mode's pentatonic scale above its own tonic (羽: 0 3 5 7 10). */
export function modeScale(mode: Mode): number[] {
  const tonic = MODE_TONIC[mode];
  const start = PENTATONIC.indexOf(tonic);
  const base = DEGREE_SEMITONES[tonic - 1]!;
  return PENTATONIC.map((_, i) => {
    const d = PENTATONIC[(start + i) % 5]!;
    return (DEGREE_SEMITONES[d - 1]! - base + 12) % 12;
  });
}

/** MIDI number of a mode's tonic in a key, in the middle octave (or `octave` away). */
export const tonicMidi = (key: Key, mode: Mode, octave = 0): number => degreeMidi(key, MODE_TONIC[mode], octave);

/** Frequency (Hz) of a MIDI number, A4 = 440. */
export const mtof = (midi: number): number => 440 * 2 ** ((midi - 69) / 12);

/** True when a MIDI pitch is one of the key's five pentatonic notes. */
export function inPentatonic(key: Key, midi: number): boolean {
  const pc = (((midi - keyMidi(key)) % 12) + 12) % 12;
  return PENTATONIC.some((d) => DEGREE_SEMITONES[d - 1] === pc);
}

/** The key's pentatonic pitches from `lo` to `hi` (MIDI, inclusive), low to high: the open strings of a zither. */
export function pentatonicRange(key: Key, lo: number, hi: number): number[] {
  const out: number[] = [];
  for (let m = Math.ceil(lo); m <= hi; m++) if (inPentatonic(key, m)) out.push(m);
  return out;
}

/** The pentatonic pitch `steps` scale steps away from `midi` (which should be in the scale). */
export function pentatonicStep(key: Key, midi: number, steps: number): number {
  let m = midi;
  const dir = Math.sign(steps);
  for (let n = Math.abs(steps); n > 0; ) {
    m += dir;
    if (inPentatonic(key, m)) n--;
  }
  return m;
}

// ───────── jianpu tokens of the pentatonic, for the accompaniment helpers (compose.ts) ─────────

/** Index of a pentatonic jianpu token on the scale ("6," → −1, "1" → 0, "1'" → 5). */
export function pentaIndex(tok: string): number {
  const m = /^([1-7])([',]*)$/.exec(tok);
  if (!m) throw new Error(`not a scale note: ${tok}`);
  const pos = PENTATONIC.indexOf(Number(m[1]));
  if (pos < 0) throw new Error(`not pentatonic: ${tok}`);
  let oct = 0;
  for (const c of m[2]!) oct += c === "'" ? 1 : -1;
  return oct * 5 + pos;
}

/** The jianpu token of a pentatonic index (inverse of `pentaIndex`). */
export function pentaToken(index: number): string {
  const oct = Math.floor(index / 5);
  const pos = index - oct * 5;
  return `${PENTATONIC[pos]}${(oct > 0 ? "'" : ',').repeat(Math.abs(oct))}`;
}
